import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  IconButton,
  Switch,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckCircleOutline, WarningOutlined } from '@mui/icons-material';
import { useUpdateAdcConfigMutation } from '../../../features/organizations/organizationsApiSlice';
import { useGetDataAssetByNameQuery } from '../../../features/data_assets/dataAssetsApiSlice';
import { useNavigate } from 'react-router-dom';

// Custom switch styling: bigger size, red track when off, green track when on.
const CustomSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== 'colors',
})(({ theme, colors }) => ({
  width: 50,
  height: 30,
  padding: 4,
  '& .MuiSwitch-switchBase': {
    padding: 4,
    margin: 0,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: theme.palette.common.white,
      '& + .MuiSwitch-track': {
        backgroundColor: colors.greenAccent[500],
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 20 / 2,
    backgroundColor: colors.redAccent[500],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));


const AdcSettings = ({ organization, colors, whitelabel_config }) => {
  const navigate = useNavigate();
  const [updateAdcConfig] = useUpdateAdcConfigMutation();

  // Fetch Universal Person data
  const { data: universalPersonData, isLoading: isLoadingUniversalPerson } = useGetDataAssetByNameQuery("Universal Person");


  console.log("ADC Config: ", organization.adc_config);
  const adc = organization.adc_config ?? {};

  // Original state for comparison
  const originalState = {
    adc_enabled: !!organization.adc_enabled,

    // strings
    up_version: adc.up_version ?? "2.16.0",
    adc_resolution_version: adc.adc_resolution_version ?? "2.0",

    // org-level toggles
    beeswax_cookie_sync: !!organization.beeswax_cookie_sync,
    bito_hem_enabled: !!organization.bito_hem_enabled,
    whitelabel_enabled: !!organization.whitelabel_enabled,
    hem_contribution_tag: !!organization.hem_contribution_tag,

    // adc_config toggles (IMPORTANT: coalesce to booleans)
    up_id_output: adc.up_id_output ?? false,
    filtered_cookie_pool: adc.filtered_cookie_pool ?? true,   // ✅ schema default is true
    bot_filtering: adc.bot_filtering ?? false,
    real_time_resolution: adc.real_time_resolution ?? false,
    redirect_excluded: adc.redirect_excluded ?? false,
    intent_excluded: adc.intent_excluded ?? false,
    block_all_requests: adc.block_all_requests ?? false,
    deliver_referrer: adc.deliver_referrer ?? false,

    // arrays
    hash_types: adc.hash_types ?? ["sha256"],
  };

  console.log("originalState: ", originalState);


  // Booleans converted to true/false state
  const [adcEnabled, setAdcEnabled] = useState(!!originalState.adc_enabled);
  const [beeswaxCookieSync, setBeeswaxCookieSync] = useState(!!originalState.beeswax_cookie_sync);
  const [adcBotFiltering, setAdcBotFiltering] = useState(!!originalState.bot_filtering);
  const [adcUpIDs, setAdcUpIDs] = useState(!!originalState.up_id_output);
  const [bitoHemEnabled, setBitoHemEnabled] = useState(!!originalState.bito_hem_enabled);
  const [realTimeResolution, setRealTimeResolution] = useState(!!originalState.real_time_resolution);
  const [redirectExcluded, setRedirectExcluded] = useState(!!originalState.redirect_excluded);
  const [whitelabelEnabled, setWhitelabelEnabled] = useState(!!originalState.whitelabel_enabled);
  const [contributionTagSwitch, setContributionTagSwitch] = useState(!!originalState.hem_contribution_tag);
  const [filteredCookiePool, setFilteredCookiePool] = useState(!!originalState.filtered_cookie_pool);
  const [intentFeedExclusion, setIntentFeedExclusion] = useState(!!originalState.intent_excluded);
  const [blockAllRequests, setBlockAllRequests] = useState(organization.adc_config?.block_all_requests ?? false);
  const [deliverReferrer, setDeliverReferrer] = useState(!!originalState.deliver_referrer);


  // Other states
  const [upVersionSelected, setUpVersionSelected] = useState(originalState.up_version);
  const [adcResolutionVersion, setAdcResolutionVersion] = useState(originalState.adc_resolution_version);

  const [hashTypesSelected, setHashTypesSelected] = useState(originalState.hash_types);
  const [changes, setChanges] = useState([]);
  // Dialog state(s)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saveConfirmDialogOpen, setSaveConfirmDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // If "Redirect Excluded" is on, disable real-time resolution.
  useEffect(() => {
    if (redirectExcluded) {
      setRealTimeResolution(false);
    }
  }, [redirectExcluded]);

  // Helper function for comparing arrays
  const arraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((val, index) => val === arr2[index]);
  };

  // Build a summary of changes for confirmation
  const getChangesSummary = (wasDefaulted = false) => {
    const newState = {
      adc_enabled: adcEnabled,
      up_version: upVersionSelected,
      adc_resolution_version: adcResolutionVersion,
      beeswax_cookie_sync: beeswaxCookieSync,
      bito_hem_enabled: bitoHemEnabled,
      up_id_output: adcUpIDs,
      filtered_cookie_pool: filteredCookiePool,
      bot_filtering: adcBotFiltering,
      real_time_resolution: realTimeResolution,
      whitelabel_enabled: whitelabelEnabled,
      hem_contribution_tag: contributionTagSwitch,
      redirect_excluded: redirectExcluded,
      hash_types: hashTypesSelected,
      intent_excluded: intentFeedExclusion,
      block_all_requests: blockAllRequests,
      deliver_referrer: deliverReferrer,

    };

    const fieldLabelMap = {
      adc_enabled: "ADC Enabled",
      up_version: "Universal Person Version",
      beeswax_cookie_sync: "Beeswax Cookie Sync",
      bito_hem_enabled: "Bito HEM Enabled",
      up_id_output: "ADC UP IDs",
      filtered_cookie_pool: "1:1 Cookie Pool",
      bot_filtering: "ADC Bot Filtering",
      real_time_resolution: "Real Time Resolution",
      whitelabel_enabled: "Whitelabel Enabled",
      hem_contribution_tag: "Contribution Tag",
      redirect_excluded: "Redirect Excluded",
      intent_excluded: "Intent Feed Exclusion",
      block_all_requests: "Block All Requests",
      adc_resolution_version: "ADC Resolution Version",
      deliver_referrer: "Deliver Referrer",
    };

    const changesArr = [];
    Object.entries(newState).forEach(([key, value]) => {
      if (key === "hash_types") return;
      if (value !== originalState[key]) {
        changesArr.push({
          field: fieldLabelMap[key] || key,
          oldValue: String(originalState[key] !== undefined ? originalState[key] : "Not Set/False"),
          newValue: String(value),
        });
      }
    });
    if (wasDefaulted) {
      changesArr.push({
        field: "Hash Types",
        oldValue: originalState.hash_types.length ? originalState.hash_types.join(", ") : "Not Set",
        newValue: "Defaulted to: sha256.",
        note: "None cannot be saved, so it was defaulted to sha256.",
      });
    } else if (!arraysEqual(hashTypesSelected, originalState.hash_types)) {
      changesArr.push({
        field: "Hash Types",
        oldValue: originalState.hash_types.length ? originalState.hash_types.join(", ") : "Not Set",
        newValue: hashTypesSelected.join(", "),
      });
    }
    return changesArr;
  };

  const handleSaveChangesClick = () => {
    let wasDefaulted = false;
    if (hashTypesSelected.length === 0) {
      setHashTypesSelected(["sha256"]);
      wasDefaulted = true;
    }
    const summary = getChangesSummary(wasDefaulted);
    setChanges(summary);
    setErrorMessage(null);
    setSaveConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setErrorMessage(null);
    const adcSettings = {
      id: organization.organization_id,
      adcConfig: {
        adc_enabled: adcEnabled,
        up_id_output: adcUpIDs,
        filtered_cookie_pool: filteredCookiePool,
        real_time_resolution: realTimeResolution,
        beeswax_cookie_sync: beeswaxCookieSync,
        bito_hem_enabled: bitoHemEnabled,
        whitelabel_enabled: whitelabelEnabled,
        whitelabel_config: whitelabelEnabled ? whitelabel_config : [],
        bot_filtering: adcBotFiltering,
        up_version: upVersionSelected,
        adc_resolution_version: adcResolutionVersion,
        redirect_excluded: redirectExcluded,
        hash_types: hashTypesSelected,
        block_all_requests: blockAllRequests,
        intent_excluded: intentFeedExclusion,
        hem_contribution_tag: contributionTagSwitch,
        deliver_referrer: deliverReferrer,
      },
    };

    try {
      await updateAdcConfig(adcSettings).unwrap();
      setTimeout(() => {
        setSaveSuccess(true);
        setIsSaving(false);
        setTimeout(() => {
          setSaveConfirmDialogOpen(false);
          navigate(`/console/organization/${organization.organization_id}`);
        }, 1500);
      }, 1000);
    } catch (err) {
      console.error(err);
      setErrorMessage(err?.data?.message || "An error occurred while saving changes.");
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Top: Universal Person Version */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" color={colors.grey[100]}>
          ADC Settings
        </Typography>
      </Box>

      {/* Grid: 12 slots (11 switches  hash-types) */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>ADC Enabled</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={adcEnabled} onChange={(e) => setAdcEnabled(e.target.checked)} colors={colors} />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>Beeswax Cookie Sync</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={beeswaxCookieSync} onChange={(e) => setBeeswaxCookieSync(e.target.checked)} colors={colors} />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>ADC Bot Filtering</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={adcBotFiltering} onChange={(e) => setAdcBotFiltering(e.target.checked)} colors={colors} />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>ADC UP IDs</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={adcUpIDs} onChange={(e) => setAdcUpIDs(e.target.checked)} colors={colors} />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>Bito HEM Enabled</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={bitoHemEnabled} onChange={(e) => setBitoHemEnabled(e.target.checked)} colors={colors} />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>Real Time Resolution</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch
              checked={realTimeResolution}
              onChange={(e) => setRealTimeResolution(e.target.checked)}
              disabled={redirectExcluded}
              colors={colors}
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>Redirect Excluded</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={redirectExcluded} disabled onChange={(e) => setRedirectExcluded(e.target.checked)} colors={colors} />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>Intent Feed Exclusion</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={intentFeedExclusion} onChange={(e) => setIntentFeedExclusion(e.target.checked)} colors={colors} />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>Whitelabel Enabled</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={whitelabelEnabled} onChange={(e) => setWhitelabelEnabled(e.target.checked)} colors={colors} />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>1:1 Cookie Pool</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch checked={filteredCookiePool} onChange={(e) => setFilteredCookiePool(e.target.checked)} colors={colors} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>Deliver Referrer</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch
              checked={deliverReferrer}
              onChange={(e) => setDeliverReferrer(e.target.checked)}
              colors={colors}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography color={colors.grey[200]}>Block All Requests</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <CustomSwitch
              checked={blockAllRequests}
              onChange={e => setBlockAllRequests(e.target.checked)}
              disabled={adcEnabled} // only enable when ADC itself is turned OFF
              colors={colors}
            />
          </Grid>
          {/* —— now slot 12: HASH TYPES in the same grid —— */}
          <Grid item xs={6} sm={2}>
            <Typography color={colors.grey[300]}>Hash Types</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Autocomplete
              multiple
              options={["sha256", "md5"]}
              value={hashTypesSelected}
              onChange={(e, v) => setHashTypesSelected(v)}
              renderInput={(params) => <TextField {...params} variant="standard" />}
            />
          </Grid>
          {/* row: Universal Person + ADC Resolution */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box >
                <Typography variant="h6" color={colors.grey[300]}>
                  Universal Person Version:
                </Typography>
                {isLoadingUniversalPerson ? (
                  <Typography variant="body2" color={colors.grey[500]}>
                    Loading versions...
                  </Typography>
                ) : (
                  <Select
                    fullWidth
                    value={upVersionSelected}
                    onChange={(e) => setUpVersionSelected(e.target.value)}
                  >
                    {universalPersonData?.versions
                      ?.slice()
                      .sort((a, b) => {
                        const [aMajor, aMinor, aPatch] = a.version.split('.').map(Number);
                        const [bMajor, bMinor, bPatch] = b.version.split('.').map(Number);
                        return aMajor - bMajor || aMinor - bMinor || aPatch - bPatch;
                      })
                      .slice(-3)
                      .map((version) => (
                        <MenuItem key={version.version} value={version.version}>
                          {version.version}
                        </MenuItem>
                      ))}
                    {!universalPersonData?.versions?.some((v) => v.version === "2.11.0") && (
                      <MenuItem key="2.11.0" value="2.11.0">
                        2.11.0
                      </MenuItem>
                    )}
                  </Select>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="h6" color={colors.grey[300]}>
                ADC Resolution Version:
              </Typography>
              <Select
                fullWidth
                value={adcResolutionVersion}
                onChange={(e) => setAdcResolutionVersion(e.target.value)}
              >
                <MenuItem value="1.0">1.0&nbsp;(Snowflake)</MenuItem>
                <MenuItem value="2.0">2.0&nbsp;(Postgres)</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
      </Box>



      {/* Bottom Right Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <Button variant="contained" color="info" sx={{ mb: 1 }} onClick={() => setDialogOpen(true)}>
          Contribution Tag
        </Button>
        <Button variant="contained" color="success" onClick={handleSaveChangesClick}>
          Save Changes
        </Button>
      </Box>

      {/* Contribution Tag Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Contribution Tag Information</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body1">
                This product uses innovative techniques to collect emails on various web pages.
                It utilizes a blend of machine learning algorithms and traditional web scraping methods
                to identify and extract email addresses while maintaining user privacy and adhering
                to data protection regulations.
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <CustomSwitch
                    checked={contributionTagSwitch}
                    onChange={(e) => setContributionTagSwitch(e.target.checked)}
                    name="contributionTagSwitch"
                    color="primary"
                    colors={colors}
                  />
                }
                label="Enable/Disable"
              />
              {contributionTagSwitch !== organization.hem_contribution_tag && (
                <Paper sx={{ backgroundColor: "#f5f5f5", p: 1, mt: 1, display: 'flex', alignItems: 'center' }}>
                  <WarningOutlined sx={{ color: colors.redAccent[500], mr: 1 }} />
                  <Typography variant="body2" sx={{ color: "red" }}>
                    Changes Haven't Been Applied
                  </Typography>
                </Paper>
              )}
              {contributionTagSwitch && (
                <Paper sx={{ backgroundColor: "#424242", p: 1, mt: 1 }}>
                  <Typography variant="body1" sx={{ color: "#ffffff", fontWeight: "bold", overflowWrap: 'break-word' }}>
                    Contribution Tag URL: https://a.usbrowserspeed.com/metrics.js?pid={organization.pid}
                  </Typography>
                  <Paper sx={{ mt: 1, backgroundColor: '#3a3a3a', p: 1 }}>
                    <Typography variant="body1" sx={{ color: "#ffffff", fontWeight: "bold", overflowWrap: 'break-word' }}>
                      Example Code:
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        color: "#ffffff",
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {`<script type="text/javascript" src="https://a.usbrowserspeed.com/metrics.js?pid=${organization.pid}"></script>`}
                    </Box>
                  </Paper>
                </Paper>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" color="secondary">
            Close
          </Button>
          <Button onClick={handleSaveChangesClick} variant="outlined" sx={{ color: colors.greenAccent[700], borderColor: colors.greenAccent[700] }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Confirmation Dialog */}
      <Dialog open={saveConfirmDialogOpen} onClose={() => setSaveConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          {isSaving ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minWidth="200px" minHeight="100px">
              {saveSuccess ? (
                <>
                  <CheckCircleOutline sx={{ color: 'green', fontSize: '3rem', mb: 1 }} />
                  <Typography variant="h6">Save Successful!</Typography>
                </>
              ) : (
                <>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ mt: 2 }}>Saving...</Typography>
                </>
              )}
            </Box>
          ) : errorMessage ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minWidth="200px" minHeight="100px">
              <WarningOutlined sx={{ color: 'red', fontSize: '3rem', mb: 1 }} />
              <Typography variant="h6" color="red" sx={{ mt: 1, mb: 2 }}>{errorMessage}</Typography>
              <Typography variant="body1">Please try again or contact support if the issue persists.</Typography>
            </Box>
          ) : (
            <>
              {changes.length === 0 ? (
                <Typography variant="body1">No changes detected.</Typography>
              ) : (
                <Box>
                  <Typography variant="body1" paragraph>
                    The following fields have changed:
                  </Typography>
                  {changes.map((change, index) => (
                    <Typography key={index} variant="body2">
                      <strong>{change.field}:</strong> {change.oldValue} → {change.newValue}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!isSaving && !saveSuccess && !errorMessage && (
            <>
              <Button onClick={() => setSaveConfirmDialogOpen(false)} variant="outlined" color="secondary">
                Cancel
              </Button>
              {changes.length > 0 && (
                <Button onClick={handleConfirmSave} variant="outlined" sx={{ color: 'green', borderColor: 'green' }}>
                  Confirm
                </Button>
              )}
            </>
          )}
          {errorMessage && (
            <Button onClick={() => setSaveConfirmDialogOpen(false)} variant="outlined" color="secondary">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdcSettings;
